import { defineStore } from 'pinia';

import type {
  ConsentDeleteDTO,
  ConsentDTO,
  ConversationResultDTO,
  EventResultDTO,
  GeofenceDeleteDTO,
  GeofenceRuleDTO,
  MapDisplaySettingsDTO,
  NotificationCreateEventData,
  NotificationResultDTO,
  OperationDeleteDTO,
  OperationLocalRoleUpdatedEventDTO,
  OperationMemberRemoveData,
  OperationResultDTO,
  OperationTrackerDetailsEventDTO,
  OperationUserDTO,
  SymbolPackDeleteResultDTO,
  SymbolPackDTO,
  TrackerDeleteDTO,
  UpdateSettingDTO,
  UserClearLocationData,
  UserDetailsUpdateEventDTO,
} from '@nora-monorepo/nora-api-client';
import {
  EventPattern,
} from '@nora-monorepo/nora-api-client';
import { isNonEmptyString } from '@nora-monorepo/shared';
import {
  useConfigStore,
  useGenericUserStore,
  useSseStore,
} from '@nora-monorepo/ui-shared';

import { useAdminSymbolPackStore } from '#nora-ui/stores/admin-panel/admin-symbol-pack-store';
import { useAdminTrackerStore } from '#nora-ui/stores/admin-panel/admin-tracker-store';
import { useConsentStore } from '#nora-ui/stores/consent-store';
import { useGeofenceStore } from '#nora-ui/stores/geofence-store';
import { useNotificationStore } from '#nora-ui/stores/notification-store';
import { useOperationStore } from '#nora-ui/stores/operation-store';
import { useSymbolStore } from '#nora-ui/stores/symbol-store';

export const useNoraSseStore = defineStore('noraSseStore', () => {
  const sseStore = useSseStore();
  const consentStore = useConsentStore();
  const notificationStore = useNotificationStore();
  const operationStore = useOperationStore();
  const adminTrackerStore = useAdminTrackerStore();
  const adminSymbolPackStore = useAdminSymbolPackStore();
  const symbolStore = useSymbolStore();
  const geofenceStore = useGeofenceStore();
  const configStore = useConfigStore();
  const userStore = useGenericUserStore();

  async function startListening(): Promise<void> {
    await sseStore.startListening();
  }

  sseStore.addEventHandler(
    EventPattern.OperationCreate,
    async (event: EventResultDTO) => {
      const data = event.eventData;
      const operationData = data as OperationResultDTO;

      await operationStore.eventCreateOperation(operationData);
    },
  );

  sseStore.addEventHandler(EventPattern.OperationNameUpdated, async (event: EventResultDTO) => {
    const data = event.eventData;
    const nameUpdateData = data as { newName: string; operationId: string };

    await operationStore.eventUpdateOperationName(nameUpdateData);
  });

  /**
   * Event handler for when the local roles of an operation are updated. This will update the local roles of the operation in the store for the users that were updated.
   */
  sseStore.addEventHandler(EventPattern.OperationLocalRoleUpdated, async (event: EventResultDTO) => {
    const data = event.eventData;
    const localRoleUpdateData = data as OperationLocalRoleUpdatedEventDTO;
    const updatedList = localRoleUpdateData.updated;

    await operationStore.eventUpdateOperationLocalRoles(updatedList);
  });

  sseStore.addEventHandler(
    EventPattern.OperationUpdate,
    async (event: EventResultDTO) => {
      const data = event.eventData;
      const operationData = data as OperationResultDTO;

      await operationStore.eventUpdateOperation(operationData);
    },
  );

  sseStore.addEventHandler(
    EventPattern.OperationInviteSent,
    async () => {
      await consentStore.eventInviteReceived();
    },
  );

  sseStore.addEventHandler(
    EventPattern.OperationUpdateMember,
    async (event: EventResultDTO) => {
      const data = event.eventData;
      const operationData = data as OperationUserDTO;

      await operationStore.eventUpdateOperationMember(operationData);
    },
  );

  sseStore.addEventHandler(
    EventPattern.OperationMemberRemove,
    async (event) => {
      const data = event.eventData;
      const operationData = data as OperationMemberRemoveData;

      await operationStore.eventOperationRemoveMember(operationData);
    },
  );

  sseStore.addEventHandler(
    EventPattern.OperationDelete,
    async (event: EventResultDTO) => {
      const data = event.eventData;
      const operationData = data as OperationDeleteDTO;

      await operationStore.eventDeleteOperation(operationData);
    },
  );

  sseStore.addEventHandler(
    EventPattern.ConsentCreate,
    async (event: EventResultDTO) => {
      const data = event.eventData;
      const consentData = data as ConsentDTO;

      await consentStore.eventCreateConsent(consentData);
    },
  );

  sseStore.addEventHandler(
    EventPattern.ConsentUpdate,
    async (event: EventResultDTO) => {
      const data = event.eventData;
      const consentData = data as ConsentDTO;

      await consentStore.eventUpdateConsent(consentData);
    },
  );

  sseStore.addEventHandler(
    EventPattern.ConsentDelete,
    async (event: EventResultDTO) => {
      const data = event.eventData;
      const consentData = data as ConsentDeleteDTO;

      await consentStore.eventDeleteConsent(consentData);
    },
  );

  sseStore.addEventHandler(
    EventPattern.TrackerCreate,
    async (event: EventResultDTO) => {
      const data = event.eventData;
      const trackerData = data as OperationTrackerDetailsEventDTO;

      await operationStore.eventCreateTracker(trackerData);
    },
  );

  sseStore.addEventHandler(
    EventPattern.TrackerUpdate,
    async (event: EventResultDTO) => {
      const data = event.eventData;
      const trackerData = data as OperationTrackerDetailsEventDTO;

      await operationStore.eventUpdateTracker(trackerData);
    },
  );

  sseStore.addEventHandler(
    EventPattern.TrackerDelete,
    async (event: EventResultDTO) => {
      const data = event.eventData;
      const trackerData = data as TrackerDeleteDTO;

      await operationStore.eventDeleteTracker(trackerData);
      await adminTrackerStore.eventDeleteTracker(trackerData.id);
    },
  );

  sseStore.addEventHandler(
    EventPattern.NotificationCreate,
    async (event: EventResultDTO) => {
      const data = event.eventData;
      const messageData = data as NotificationCreateEventData;

      await notificationStore.fetchNotification(messageData.id);
    },
  );

  sseStore.addEventHandler(
    EventPattern.NotificationUpdate,
    async (event: EventResultDTO) => {
      const data = event.eventData;
      const messageData = data as NotificationResultDTO;

      notificationStore.updateOrAddLocalNotification(messageData);
    },
  );

  sseStore.addEventHandler(
    EventPattern.GeofenceCreate,
    async (event: EventResultDTO) => {
      const data = event.eventData;
      const ruleData = data as GeofenceRuleDTO;

      await geofenceStore.eventAddGeofenceRule(ruleData);
    },
  );

  sseStore.addEventHandler(
    EventPattern.GeofenceUpdate,
    async (event: EventResultDTO) => {
      const data = event.eventData;
      const ruleData = data as GeofenceRuleDTO;

      await geofenceStore.eventUpdateGeofenceRule(ruleData);
    },
  );

  sseStore.addEventHandler(
    EventPattern.GeofenceDelete,
    async (event: EventResultDTO) => {
      const data = event.eventData;
      const ruleData = data as GeofenceDeleteDTO;

      await geofenceStore.eventRemoveGeofenceRule(ruleData);
    },
  );

  sseStore.addEventHandler(
    EventPattern.GeofenceActivate,
    async (event: EventResultDTO) => {
      const data = event.eventData;
      const ruleData = data as GeofenceRuleDTO;

      await geofenceStore.eventGeofenceRuleActivate(ruleData);
    },
  );

  sseStore.addEventHandler(
    EventPattern.GeofenceDeactivate,
    async (event: EventResultDTO) => {
      const data = event.eventData;
      const ruleData = data as GeofenceRuleDTO;

      await geofenceStore.eventGeofenceRuleDeactivate(ruleData);
    },
  );

  sseStore.addEventHandler(
    EventPattern.UserSettingsUpdate,
    async (event: EventResultDTO) => {
      const data = event.eventData;
      const updateSettingsData = data as UpdateSettingDTO;

      await configStore.eventUpdateUserSettings(updateSettingsData);
    },
  );

  /**
   * Represents an Event where the Map Settings of the User has been updated.
   *
   * The event is primarily sent from the backend on POST to the map display settings endpoint,
   * but may also be sent by Mobile.
   *
   * We listen to the map settings SSE event because we want as much consistency across devices
   * as possible. That means that whenever a user changes the settings on mobile, the changes
   * should be reflected on web, and vice versa.
   *
   * Note that this will not guarantee consistency across devices all the time. For example, if a
   * device is offline - or otherwise operate in a degraded network environment - that event
   * will or may never reach that device.
   */
  sseStore.addEventHandler(EventPattern.UserMapDisplaySettingsUpdate, (event: EventResultDTO) => {
    const data = event.eventData;
    const userMapDisplaySettings = data as MapDisplaySettingsDTO;

    configStore.eventUpdateMapDisplaySettings(userMapDisplaySettings);
  });

  sseStore.addEventHandler(
    EventPattern.UserClearLocation,
    (event: EventResultDTO) => {
      const data = event.eventData as UserClearLocationData;

      if (data.operationId === undefined) {
        userStore.clearLastTrack(data.userId);
      }

      operationStore.clearUserLastTrack(data.userId, data.operationId);
    },
  );

  sseStore.addEventHandler(
    EventPattern.UserDetailsUpdate,
    async (event: EventResultDTO) => {
      const data = event.eventData;
      const updateUserDetails = data as UserDetailsUpdateEventDTO;

      await operationStore.eventUpdateUserDetails(updateUserDetails);
      await userStore.updateUserDetailsEvent(updateUserDetails);
    },
  );

  sseStore.addEventHandler(
    EventPattern.ConversationCreate,
    async (event) => {
      const data: unknown = event.eventData;
      const conversationDTO = data as ConversationResultDTO;

      if (isNonEmptyString(conversationDTO.operationId)) {
        await operationStore.eventUpdateOperationConversationList(
          conversationDTO.operationId,
        );
      }
    },
  );

  sseStore.addEventHandler(
    EventPattern.SymbolPackCreate,
    async (event: EventResultDTO) => {
      const data = event.eventData;
      const symbolPackData = data as SymbolPackDTO;

      adminSymbolPackStore.eventCreateSymbolPack(symbolPackData);
      await symbolStore.replaceSymbolPackCache(symbolPackData);
    },
  );

  sseStore.addEventHandler(
    EventPattern.SymbolPackUpdate,
    async (event: EventResultDTO) => {
      const data = event.eventData;
      const symbolPackData = data as SymbolPackDTO;

      adminSymbolPackStore.eventUpdateSymbolPack(symbolPackData);
      await symbolStore.upsertSymbolPackCache(symbolPackData);
    },
  );

  sseStore.addEventHandler(
    EventPattern.SymbolPackDelete,
    async (event: EventResultDTO) => {
      const data = event.eventData;
      const deleteResult = data as SymbolPackDeleteResultDTO;

      adminSymbolPackStore.eventDeleteSymbolPack(deleteResult);
      await symbolStore.removeSymbolPackFromCache(deleteResult.id);
    },
  );

  return {
    startListening,
  };
});
